/* Rebuild the chain under a condition (Ale ruling 2026-09-03, C8:
 * "the record needs an AS-OF chain rebuild: same idea as the evidenced
 * toggle … SHIP THE TWO TOGETHER, they share one concept — rebuild the
 * chain under a condition").
 *
 * One toggle, two modes, one shape:
 *
 *   EVIDENCED ONLY — the fund rebuilt from the objects whose demand is
 *     MET (a demand waived by an officer counts as met — waived = proven
 *     = evidenced, full stop, Ale 14c). An object that carries NO demand
 *     STANDS ("nothing asked, nothing missing", Ale 14a). An entity
 *     without an evidenced existence (E1) drops with its subtree; a
 *     holding whose balance (B1 / B2) is unmet drops with its movements;
 *     a held instrument whose issued balance (B3) is unmet keeps its box
 *     but its ISSUED figure blanks; a movement whose paper (M1) is unmet
 *     drops. BALANCE rebuilds from the evidenced balance paper + the
 *     evidenced movements only.
 *
 *   AS OF A DATE — the fund as the record knew it at a chosen date. Since
 *     R3 (Ale 2026-09-04) this is the ENGINE's one rule (funds/as_of_scope:
 *     the latest recorded balance dated ≤ D rolled forward with the
 *     movements up to D; a holding with no dated evidence ≤ D has no
 *     balance as of D), served by GET /api/funds/{ref}/as-of. The
 *     frontend no longer computes it; it only knows which dates the
 *     record holds (`historyDates`, `stepDates`) for the picker.
 *
 * The toggle changes the VARIABLE (which objects exist, which figures
 * they carry) and hands the SAME payload shape back — the existing
 * compile (prose, chain status, EV, RECO, the diagram, the drawer) then
 * runs unchanged. No second vocabulary.
 *
 * REACH (Ale 14b, approved 11:0x): the rebuilt payload feeds the three
 * tab contents (Overview, Record, Evidence) and the drawer. The counts
 * strip, the walk/workbench queue and Export stay on the FULL record —
 * the caller keeps handing those the untouched payload.
 *
 * Pure and import-free beyond the equally pure `chainTree` helpers, so
 * the bare node runner can prove the arithmetic — see
 * tests/unit/rebuildRecord.test.mjs. Every kept object is a SPREAD COPY,
 * so fields this module never names (path, seq, ev_*, …) pass through
 * untouched and the caller may cast the result back to its payload. */

export type RebuildMode = 'full' | 'evidenced' | 'asof'

/** The structural subset of the record-table payload the rebuild needs.
 *  Kept deliberately loose (`string` states, optional extras) so the real
 *  `RecordTablePayload` satisfies it and casts back cleanly. */
export interface RbSegment {
  group: string
  holder_id: string
  position_id: string
  position_ids: string[]
  instrument_id: string | null
  issuer_id: string | null
  units_held: number | null
  committed_amount: number | null
  drawn_amount: number | null
  undrawn_amount: number | null
  economic_pct: number | null
  as_of: string | null
  /** R3a: how the figure was fixed — "balance" (a statement), "balance+trades"
   *  (a statement rolled forward), "trades" (movements only, no statement).
   *  Absent on the live record. */
  balance_source?: string | null
  state: string
}

export interface RbTrade {
  id: string
  position_id: string | null
  instrument_id: string | null
  units_delta: number | null
  effective_date: string | null
  state: string
}

export interface RbEntity {
  id: string
  state: string
}

export interface RbInstrument {
  id: string
  outstanding_units: number | null
  state: string
}

export interface RbPosition {
  id: string
  state: string
}

export interface RbMatrixRow {
  object_type: string
  object_id: string
  obligation_code: string
  status: string
}

export interface RbTable {
  segments: RbSegment[]
  entities: RbEntity[]
  instruments: RbInstrument[]
  positions: RbPosition[]
  trades: RbTrade[]
}

/** An object the rebuild left out of the view, for the "what changed"
 *  read-outs and the chain-status "left out" lines. */
export interface Dropped {
  kind: 'entity' | 'instrument' | 'position' | 'trade'
  id: string
  reason: string
}

export interface Rebuilt<T extends RbTable, M extends RbMatrixRow> {
  table: T
  /** The proof matrix narrowed to the demands of standing objects — the
   *  chain status recounts over this, so "N of M proven" is of the
   *  rebuilt record; unmet-but-standing demands (a recommended M2) remain
   *  and are the gaps it names. */
  matrix: M[]
  mode: RebuildMode
  asOf: string | null
  dropped: Dropped[]
}

const MET = new Set(['satisfied', 'waived'])
const confirmed = (state: string): boolean => state !== 'proposed'

type CodeState = 'met' | 'unmet' | 'absent'

/** demands[objectId] → the object's matrix rows. */
function demandIndex(matrix: RbMatrixRow[]): Map<string, RbMatrixRow[]> {
  const m = new Map<string, RbMatrixRow[]>()
  for (const r of matrix) {
    const list = m.get(r.object_id)
    if (list) list.push(r)
    else m.set(r.object_id, [r])
  }
  return m
}

/** How one obligation stands on an object: MET when every row of that
 *  code is satisfied/waived, UNMET when any is open, ABSENT when the
 *  object carries no such demand (14a — nothing asked). */
function codeState(rows: RbMatrixRow[] | undefined, code: string): CodeState {
  if (!rows) return 'absent'
  const of = rows.filter((r) => r.obligation_code === code)
  if (of.length === 0) return 'absent'
  return of.every((r) => MET.has(r.status)) ? 'met' : 'unmet'
}

/** A demand drops its object only when it is UNMET — an ABSENT demand
 *  stands (14a). */
const stands = (s: CodeState): boolean => s !== 'unmet'

/** The one demand that governs a holding's existence: B2 for a
 *  commitment package, B1 otherwise. */
function balanceCode(seg: RbSegment): 'B1' | 'B2' {
  return seg.committed_amount !== null ? 'B2' : 'B1'
}

/** The demand rows across ALL of a leg's records (a package has two). */
function legRows(seg: RbSegment, demands: Map<string, RbMatrixRow[]>): RbMatrixRow[] {
  const ids = seg.position_ids.length ? seg.position_ids : [seg.position_id]
  return ids.flatMap((id) => demands.get(id) ?? [])
}

/* ── EVIDENCED ONLY ─────────────────────────────────────────────────── */

function rebuildEvidenced<T extends RbTable, M extends RbMatrixRow>(
  table: T,
  matrix: M[]
): Rebuilt<T, M> {
  const demands = demandIndex(matrix)
  const dropped: Dropped[] = []

  // Entities: an entity stands when its existence (E1) is met or unasked.
  // A dropped entity takes its subtree — enforced below, where a leg drops
  // if either end sits on a dropped entity.
  const droppedEntities = new Set<string>()
  for (const e of table.entities) {
    if (!stands(codeState(demands.get(e.id), 'E1'))) {
      droppedEntities.add(e.id)
      dropped.push({ kind: 'entity', id: e.id, reason: 'its existence is not evidenced' })
    }
  }

  // Legs: keep when both ends stand and the balance (B1/B2) is met/unasked.
  const keptSegs: T['segments'] = []
  const keptPositionIds = new Set<string>()
  for (const s of table.segments) {
    if (s.group !== 'chain') {
      keptSegs.push(s) // inbound rows are out of the rebuild's scope
      continue
    }
    const holderDropped = droppedEntities.has(s.holder_id)
    const issuerDropped = s.issuer_id ? droppedEntities.has(s.issuer_id) : false
    const balanceStands = stands(codeState(legRows(s, demands), balanceCode(s)))
    if (holderDropped || issuerDropped || !balanceStands) {
      dropped.push({
        kind: 'position',
        id: s.position_id,
        reason:
          holderDropped || issuerDropped
            ? 'its entity is not evidenced'
            : 'its balance is not evidenced',
      })
      continue
    }
    keptSegs.push(s)
    for (const id of s.position_ids.length ? s.position_ids : [s.position_id])
      keptPositionIds.add(id)
  }

  // Instruments named by a kept leg (chain group only).
  const namedInstrumentIds = new Set(
    keptSegs
      .filter((s) => s.group === 'chain' && s.instrument_id)
      .map((s) => s.instrument_id as string)
  )
  const b3 = (id: string): boolean => codeState(demands.get(id), 'B3') === 'met'

  const keptInstruments: T['instruments'] = []
  const keptInstrumentIds = new Set<string>()
  const issuedBlank = new Set<string>() // kept, but ISSUED unproven
  for (const i of table.instruments) {
    const named = namedInstrumentIds.has(i.id)
    // Named by a standing holding → stays on the chain (ISSUED may blank).
    // Unnamed (the fund's own interests) → stands only when its issued
    // balance (B3) is met.
    if (!named && !b3(i.id)) {
      dropped.push({
        kind: 'instrument',
        id: i.id,
        reason: 'no evidenced holding names it and its issued balance is not on file',
      })
      continue
    }
    keptInstrumentIds.add(i.id)
    if (b3(i.id)) {
      keptInstruments.push(i)
    } else {
      issuedBlank.add(i.id)
      keptInstruments.push({ ...i, outstanding_units: null })
    }
  }

  // Movements: a movement stands when its paper (M1) is met/unasked AND it
  // sits on a kept object. M2/M3 owed never remove it.
  const keptTrades: T['trades'] = []
  const keptTradeIds = new Set<string>()
  for (const t of table.trades) {
    if (!confirmed(t.state)) continue
    const onKept = t.position_id
      ? keptPositionIds.has(t.position_id)
      : t.instrument_id
        ? keptInstrumentIds.has(t.instrument_id)
        : false
    if (!onKept) continue
    if (!stands(codeState(demands.get(t.id), 'M1'))) {
      dropped.push({ kind: 'trade', id: t.id, reason: 'its movement paper is not evidenced' })
      continue
    }
    keptTrades.push(t)
    keptTradeIds.add(t.id)
  }

  // Blank the ISSUED-derived % on a leg whose instrument's issued balance
  // is unproven (the % needs ISSUED).
  const rebuiltSegs = keptSegs.map((s) =>
    s.instrument_id && issuedBlank.has(s.instrument_id) ? { ...s, economic_pct: null } : s
  )

  const standing = new Set<string>([
    ...table.entities.filter((e) => !droppedEntities.has(e.id)).map((e) => e.id),
    ...keptPositionIds,
    ...keptInstrumentIds,
    ...keptTradeIds,
  ])

  const rebuiltTable = {
    ...table,
    segments: rebuiltSegs,
    entities: table.entities.filter((e) => !droppedEntities.has(e.id)),
    instruments: keptInstruments,
    positions: table.positions.filter((p) => keptPositionIds.has(p.id)),
    trades: keptTrades,
  } as T

  return {
    table: rebuiltTable,
    matrix: matrix.filter((r) => standing.has(r.object_id)),
    mode: 'evidenced',
    asOf: null,
    dropped,
  }
}

/* ── AS OF A DATE — served by the engine (R3, Ale 2026-09-04) ─────────
 * The frontend's own as-of arithmetic (the former `rebuildAsOf`) is
 * RETIRED: "chain_graph's effective_date filter and the frontend
 * rebuildAsOf both retired → ONE rule". The as-of view is fetched from
 * `GET /api/funds/{ref}/as-of?date=D` (FundCockpit), whose record table
 * and matrix are already the D view. What stays here is the picker's
 * knowledge of WHICH dates the record holds. */

/** One date the record holds, and what dated it: a stated balance (a
 *  statement's date), a movement, or both. */
export interface HistoryDate {
  date: string
  balance: boolean
  movement: boolean
}

/** The dates the record holds, ascending and unique — the picker's dots,
 *  its "jump to" chips and the ‹ › steps. Client-side from the payloads
 *  already fetched (the full record plus, when one is loaded, the as-of
 *  view whose legs carry earlier balance dates): a leg's AS OF is a
 *  balance date unless its figure came from movements only; a confirmed
 *  movement's effective date is a movement date. R3a serves no fund-level
 *  history list yet, so only the dates these payloads name are known. */
export function historyDates(...tables: (RbTable | null | undefined)[]): HistoryDate[] {
  const byDate = new Map<string, HistoryDate>()
  const mark = (date: string | null, kind: 'balance' | 'movement') => {
    if (!date) return
    let h = byDate.get(date)
    if (!h) {
      h = { date, balance: false, movement: false }
      byDate.set(date, h)
    }
    h[kind] = true
  }
  for (const t of tables) {
    if (!t) continue
    for (const s of t.segments) {
      if (s.group !== 'chain') continue
      mark(s.as_of, s.balance_source === 'trades' ? 'movement' : 'balance')
    }
    for (const tr of t.trades) if (confirmed(tr.state)) mark(tr.effective_date, 'movement')
  }
  return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
}

/** The ‹ › steps from `current` (null = today) over the dates the record
 *  holds, never into the future: prev = the latest date before it, next =
 *  the earliest date after it — or today itself when the record holds
 *  nothing later, so the last step forward is always the door home. */
export function stepDates(
  dates: string[],
  current: string | null,
  today: string
): { prev: string | null; next: string | null } {
  const at = current ?? today
  const known = [...new Set(dates.filter((d) => d <= today))].sort()
  const before = known.filter((d) => d < at)
  const after = known.filter((d) => d > at)
  const prev = before.length ? before[before.length - 1] : null
  let next: string | null = after.length ? after[0] : null
  if (!next && current && current < today) next = today
  return { prev, next }
}

/* ── the one entry point ────────────────────────────────────────────── */

/** Rebuild the payload under `mode`. 'full' is the identity (the record
 *  as it stands); 'evidenced' changes the variable and hands the same
 *  shape back. 'asof' is NOT rebuilt here any more — the caller fetches
 *  the engine's D view and hands it through untouched (the identity). */
export function rebuildRecord<T extends RbTable, M extends RbMatrixRow>(
  table: T,
  matrix: M[],
  mode: RebuildMode
): Rebuilt<T, M> {
  if (mode === 'evidenced') return rebuildEvidenced(table, matrix)
  return { table, matrix, mode: 'full', asOf: null, dropped: [] }
}

/** The latest paper/movement date on the record — the "latest document
 *  date" preset for the as-of picker. */
export function latestRecordDate(table: RbTable): string | null {
  let best: string | null = null
  const bump = (d: string | null) => {
    if (d && (!best || d > best)) best = d
  }
  for (const s of table.segments) bump(s.as_of)
  for (const t of table.trades) if (confirmed(t.state)) bump(t.effective_date)
  return best
}
